import { useEffect, useRef } from 'react';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { BoldIcon, ItalicIcon, ListIcon, ListOrderedIcon } from 'lucide-react';
import classes from './NoteEditor.module.css';

interface Props {
  /** HTML string. Pass `''` for an empty editor. */
  value: string;
  /** Called with the editor's HTML on every change. Empty editor emits `''`. */
  onChange: (html: string) => void;
  /** Placeholder shown in an empty editor. */
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Optional aria-label for the editor body. */
  ariaLabel?: string;
}

export const noteEditorExtensions = [
  // The unused StarterKit features below are disabled to keep `getHTML()` output
  // limited to the formatting actually offered by `NoteEditorToolbar`. Without
  // this, a keyboard shortcut for e.g. headings would silently produce HTML
  // that the rest of the toolbar can't toggle off.
  StarterKit.configure({
    heading: false,
    codeBlock: false,
    blockquote: false,
    horizontalRule: false,
    strike: false,
    code: false,
  }),
];

// `NoteEditor.getHTML()` always wraps its output in one of these block tags,
// so a value that both starts and ends with a matching pair almost certainly
// came from the editor on a previous save. We require BOTH ends to match so a
// legacy plain-text note that merely mentions a tag mid-text (e.g. a legal
// note quoting `<strong>`) doesn't get reinterpreted as HTML on edit and lose
// the literal characters the user typed.
const RICH_TEXT_OPEN = /^\s*<(p|ul|ol)[\s>]/i;
const RICH_TEXT_CLOSE = /<\/(p|ul|ol)>\s*$/i;

const looksLikeRichText = (s: string): boolean =>
  RICH_TEXT_OPEN.test(s) && RICH_TEXT_CLOSE.test(s);

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Coerce a `value` prop into HTML the editor can round-trip without losing
 * structure. Rich-text notes already come in as HTML and pass through unchanged.
 * Legacy plain-text notes (saved before this component existed) get each line
 * wrapped in a `<p>`, with their literal `<`/`>` escaped, so:
 *   1. Tiptap doesn't collapse embedded `\n`s into spaces when the user opens
 *      an edit drawer, AND
 *   2. text that mentions tag-shaped substrings (`<strong>`, `<p`) survives the
 *      round trip as literal characters instead of getting reinterpreted as
 *      markup.
 *
 * Exported (rather than file-private) so `useEditableNote` can compare
 * draft vs. original under the same normalization — see the no-op-edit
 * detection there.
 */
export const normalizeContentForEditor = (value: string): string => {
  if (!value) {
    return '';
  }
  if (looksLikeRichText(value)) {
    return value;
  }
  return value
    .split('\n')
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');
};

export const NoteEditor = ({
  value,
  onChange,
  placeholder = 'Write a note…',
  disabled = false,
  autoFocus = false,
  ariaLabel,
}: Props) => {
  // Tracks the most recent string the editor and the parent agree on. Used so
  // the `value`-sync effect below can tell "the parent is echoing our own
  // onChange back to us" (skip) from "the parent set a new value externally"
  // (sync into the editor). Initialized with `value` from the first render so
  // the initial `useEditor` content set isn't immediately re-applied.
  const lastSyncedValue = useRef<string>(value);

  const editor = useEditor({
    extensions: [
      ...noteEditorExtensions,
      Placeholder.configure({ placeholder }),
    ],
    content: normalizeContentForEditor(value),
    editable: !disabled,
    // Skip autofocus when disabled — focusing a non-editable surface just to
    // bounce the caret back out is bad UX and noisy for screen readers.
    autofocus: autoFocus && !disabled ? 'end' : false,
    editorProps: ariaLabel
      ? { attributes: { 'aria-label': ariaLabel } }
      : undefined,
    onUpdate: ({ editor }) => {
      // Tiptap's `getHTML()` returns `<p></p>` for an empty editor. Collapsing
      // that to `''` lets callers keep using `value.length === 0` to gate
      // their Save buttons, and avoids saving stub markup to Firestore for
      // notes the user backspaced into nothing.
      const html = editor.isEmpty ? '' : editor.getHTML();
      lastSyncedValue.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }
    if (value === lastSyncedValue.current) {
      return;
    }
    // Second arg `false` = don't fire `onUpdate`; without it, this setContent
    // would emit the value we just received and risk a render loop.
    editor.commands.setContent(normalizeContentForEditor(value), false);
    lastSyncedValue.current = value;
  }, [value, editor]);

  // `editable` only reads from `useEditor` options at mount, so push later
  // changes through the imperative setter.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`${classes.editor} ${disabled ? classes.disabled : ''}`}>
      <NoteEditorToolbar editor={editor} disabled={disabled} />
      <EditorContent className={classes.content} editor={editor} />
    </div>
  );
};

const NoteEditorToolbar = ({
  editor,
  disabled,
}: {
  editor: Editor;
  disabled: boolean;
}) => {
  return (
    <div className={classes.toolbar} role='toolbar' aria-label='Formatting'>
      <ToolbarButton
        label='Bold'
        active={editor.isActive('bold')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='Italic'
        active={editor.isActive('italic')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='Bullet list'
        active={editor.isActive('bulletList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListIcon size={16} />
      </ToolbarButton>
      <ToolbarButton
        label='Numbered list'
        active={editor.isActive('orderedList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrderedIcon size={16} />
      </ToolbarButton>
    </div>
  );
};

const ToolbarButton = ({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => {
  return (
    <button
      type='button'
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      // `onMouseDown` with preventDefault keeps the editor's selection while
      // clicking a toolbar button — without it, the click steals focus and the
      // toggle has nothing to apply formatting to.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={`${classes.toolbarButton} ${active ? classes.active : ''}`}
    >
      {children}
    </button>
  );
};
